import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, Truck, X } from 'lucide-react';

export function DriverSelector({ drivers, loading, error, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const selected = selectedIds || [];

  useEffect(() => {
    if (!open) return;
    const closeOutside = event => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  const visibleDrivers = (drivers || []).filter(driver =>
    `${driver.username || ''} ${driver.id}`.toLowerCase().includes(search.trim().toLowerCase())
  );
  const selectedNames = selected.map(id =>
    (drivers || []).find(driver => Number(driver.id) === Number(id))?.username || `#${id}`
  );

  const toggle = id => {
    onChange(selected.includes(id) ? selected.filter(value => value !== id) : [...selected, id]);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Seleccionar conductores"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className="w-full flex items-center gap-2 text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 transition-colors text-left"
      >
        <Truck size={14} className="text-gray-400 shrink-0" />
        <span className={`flex-1 truncate ${selected.length ? 'font-semibold text-gray-700' : 'text-gray-400'}`}>
          {selected.length ? selectedNames.join(', ') : 'Todos los conductores'}
        </span>
        <ChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl z-20">
          <div className="p-2 border-b border-gray-100 relative">
            <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="search"
              aria-label="Buscar conductor"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar conductor..."
              className="w-full pl-8 pr-3 py-1.5 text-sm font-montserrat bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-brand-primary"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <p className="px-3 py-4 text-sm text-gray-400">Cargando conductores...</p>
            ) : error ? (
              <p className="px-3 py-4 text-sm text-red-600">No se pudieron cargar los conductores.</p>
            ) : visibleDrivers.length ? visibleDrivers.map(driver => (
              <label key={driver.id} className="flex items-center gap-2 px-3 py-2 text-sm font-montserrat text-gray-700 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(Number(driver.id))}
                  onChange={() => toggle(Number(driver.id))}
                  className="accent-brand-primary"
                />
                <span className="truncate">{driver.username || `Conductor #${driver.id}`}</span>
              </label>
            )) : (
              <p className="px-3 py-4 text-sm text-gray-400">Sin conductores</p>
            )}
          </div>
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border-t border-gray-100 text-sm font-montserrat text-gray-500 hover:text-brand-primary">
              <X size={13} /> Limpiar conductores
            </button>
          )}
        </div>
      )}
    </div>
  );
}
